import { useEffect } from 'react';

type AppTheme = 'light' | 'dark';

interface OfficeThemeColors {
    bodyBackgroundColor?: string;
    bodyForegroundColor?: string;
    controlBackgroundColor?: string;
    controlForegroundColor?: string;
}

const OFFICE_THEME_VARIABLES = [
    '--office-body-background',
    '--office-body-foreground',
    '--office-control-background',
    '--office-control-foreground',
    '--bg-primary',
    '--bg-secondary',
    '--bg-tertiary',
    '--bg-surface',
    '--text-primary',
    '--text-secondary',
    '--text-tertiary',
    '--border-primary',
    '--border-hover',
    '--border-subtle',
    '--sidebar-bg',
    '--sidebar-surface',
    '--sidebar-border',
    '--sidebar-text',
    '--sidebar-text-secondary',
    '--color-background',
    '--color-foreground',
    '--color-muted',
    '--color-border',
];

function normalizeColor(value: unknown): string | undefined {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed;
    if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
        const [, r, g, b] = trimmed;
        return `#${r}${r}${g}${g}${b}${b}`;
    }
    if (/^rgb(a)?\(/i.test(trimmed)) return trimmed;
    return undefined;
}

function hexToRgb(hex: string): [number, number, number] | null {
    const normalized = normalizeColor(hex);
    if (!normalized || !/^#[0-9a-fA-F]{6}$/.test(normalized)) return null;

    return [
        parseInt(normalized.slice(1, 3), 16),
        parseInt(normalized.slice(3, 5), 16),
        parseInt(normalized.slice(5, 7), 16),
    ];
}

function getRelativeLuminance(color: string): number {
    const rgb = hexToRgb(color);
    if (!rgb) return 1;

    const [r, g, b] = rgb.map((channel) => {
        const value = channel / 255;
        return value <= 0.03928
            ? value / 12.92
            : Math.pow((value + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function mixHex(first: string, second: string, secondWeight: number): string {
    const rgbA = hexToRgb(first);
    const rgbB = hexToRgb(second);
    if (!rgbA || !rgbB) return first;

    const weightB = Math.min(Math.max(secondWeight, 0), 1);
    const weightA = 1 - weightB;
    const mixed = rgbA.map((channel, index) => (
        Math.round(channel * weightA + rgbB[index] * weightB)
    ));

    return `#${mixed.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

function resolveOfficeTheme(): OfficeThemeColors | null {
    if (typeof Office === 'undefined') return null;
    const theme = (Office.context as any)?.officeTheme;
    if (!theme || typeof theme !== 'object') return null;

    return {
        bodyBackgroundColor: normalizeColor(theme.bodyBackgroundColor),
        bodyForegroundColor: normalizeColor(theme.bodyForegroundColor),
        controlBackgroundColor: normalizeColor(theme.controlBackgroundColor),
        controlForegroundColor: normalizeColor(theme.controlForegroundColor),
    };
}

function applyOfficeTheme(rawTheme: OfficeThemeColors | null) {
    const bodyBackground = rawTheme?.bodyBackgroundColor || '#ffffff';
    const bodyForeground = rawTheme?.bodyForegroundColor || '#1a1a1a';
    const resolvedTheme: AppTheme = getRelativeLuminance(bodyBackground) < 0.45 ? 'dark' : 'light';
    const fallbackControlBackground = resolvedTheme === 'dark' ? '#2a2a3c' : '#f8f9fa';
    const fallbackControlForeground = resolvedTheme === 'dark' ? '#e5e5e5' : '#1a1a1a';
    const controlBackground = rawTheme?.controlBackgroundColor || fallbackControlBackground;
    const controlForeground = rawTheme?.controlForegroundColor || fallbackControlForeground;
    const border = resolvedTheme === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.10)';
    const borderHover = resolvedTheme === 'dark' ? 'rgba(255, 255, 255, 0.22)' : 'rgba(0, 0, 0, 0.18)';
    const tertiaryBackground = mixHex(controlBackground, bodyBackground, resolvedTheme === 'dark' ? 0.22 : 0.45);
    const secondaryText = mixHex(controlForeground, bodyBackground, resolvedTheme === 'dark' ? 0.34 : 0.38);
    const tertiaryText = mixHex(controlForeground, bodyBackground, 0.55);
    const root = document.documentElement;

    root.setAttribute('data-theme', resolvedTheme);
    root.setAttribute('data-office-theme-source', 'host');
    root.classList.remove('light-theme', 'dark-theme');
    root.classList.add(`${resolvedTheme}-theme`);

    const variables: Record<string, string> = {
        '--office-body-background': bodyBackground,
        '--office-body-foreground': bodyForeground,
        '--office-control-background': controlBackground,
        '--office-control-foreground': controlForeground,
        '--bg-primary': bodyBackground,
        '--bg-secondary': controlBackground,
        '--bg-tertiary': tertiaryBackground,
        '--bg-surface': bodyBackground,
        '--text-primary': bodyForeground,
        '--text-secondary': secondaryText,
        '--text-tertiary': tertiaryText,
        '--border-primary': border,
        '--border-hover': borderHover,
        '--border-subtle': border,
        '--sidebar-bg': bodyBackground,
        '--sidebar-surface': controlBackground,
        '--sidebar-border': border,
        '--sidebar-text': bodyForeground,
        '--sidebar-text-secondary': secondaryText,
        '--color-background': bodyBackground,
        '--color-foreground': bodyForeground,
        '--color-muted': controlBackground,
        '--color-border': border,
    };

    Object.entries(variables).forEach(([name, value]) => {
        root.style.setProperty(name, value);
    });

    document.body.style.backgroundColor = bodyBackground;
    document.body.style.color = bodyForeground;
}

function clearOfficeTheme() {
    document.documentElement.removeAttribute('data-office-theme-source');
    OFFICE_THEME_VARIABLES.forEach((name) => {
        document.documentElement.style.removeProperty(name);
    });
}

export function useOfficeTheme(enabled: boolean) {
    useEffect(() => {
        if (!enabled || typeof Office === 'undefined') return undefined;

        const syncTheme = (theme?: OfficeThemeColors) => {
            applyOfficeTheme(theme || resolveOfficeTheme());
        };

        syncTheme();

        const eventType = (Office as any).EventType?.OfficeThemeChanged;
        const addHandler = (Office.context as any)?.addHandlerAsync;
        const removeHandler = (Office.context as any)?.removeHandlerAsync;

        const handleThemeChanged = (eventArgs: { officeTheme?: OfficeThemeColors }) => {
            syncTheme(eventArgs?.officeTheme);
        };

        if (eventType && typeof addHandler === 'function') {
            addHandler.call(Office.context, eventType, handleThemeChanged);
        }

        return () => {
            if (eventType && typeof removeHandler === 'function') {
                removeHandler.call(Office.context, eventType, { handler: handleThemeChanged });
            }
            clearOfficeTheme();
        };
    }, [enabled]);
}

